import React, { useState, useRef } from 'react';
import { t } from 'i18next';
import accomodationService from '@services/accomodation.service';

interface EmailSenderModalProps {
  seeModal: boolean;
  setSeeModal: (value: boolean) => void;
  id: number;
  qrCodeId: string;
}

const EmailSenderModal: React.FC<EmailSenderModalProps> = ({ 
  seeModal, 
  setSeeModal, 
  id, 
  qrCodeId 
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const validateEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleAddEmail = (emailToAdd: string): void => {
    const trimmedEmail = emailToAdd.trim();
    
    if (!trimmedEmail) return;

    if (emails.length >= 10) {
      setError('Massimo 10 indirizzi email');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError('Email non valida');
      return;
    }

    if (emails.includes(trimmedEmail)) {
      setError('Email già aggiunta');
      return;
    }

    setEmails([...emails, trimmedEmail]);
    setInputValue('');
    setError('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      handleAddEmail(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      setEmails(emails.slice(0, -1));
    }
  };

  const handleRemoveEmail = (indexToRemove: number): void => {
    setEmails(emails.filter((_, index) => index !== indexToRemove));
    setError('');
  };

  const handleSubmit = async (): Promise<void> => {
    if (emails.length === 0) {
      setError('Aggiungi almeno un indirizzo email');
      return;
    }

    try {
      const response = await accomodationService.shareAccomodation(emails, id, qrCodeId)
      if (response === 200) {
        handleClose();
      } else {
        setError('Errore durante l\'invio');
      }
    } catch (err) {
      setError('Errore di connessione');
    }
  };

  const handleClose = (): void => {
    setEmails([]);
    setInputValue('');
    setError('');
    setSeeModal(false);
  };

  if (!seeModal) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show"
        onClick={handleClose}
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          zIndex: 1040, 
          width: '100vw', 
          height: '100vh', 
          backgroundColor: 'rgba(0,0,0,0.5)' 
        }}
      />
      
      {/* Modal */}
      <div 
        className="modal fade show d-block"
        tabIndex={-1}
        style={{ zIndex: 1050 }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            {/* Modal Header */}
            <div className="modal-header">
              <h5 className="modal-title">{t("button.share_email")}</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={handleClose}
                aria-label="Close"
              />
            </div>

            {/* Modal Body */}
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label fw-medium">
                  {t("text.emails_address")} ({emails.length}/10)
                </label>
                
                <div
                  className="border rounded p-2"
                  style={{ 
                    minHeight: '120px', 
                    cursor: 'text',
                    borderColor: error ? '#dc3545' : '#dee2e6'
                  }}
                  onClick={() => inputRef.current?.focus()}
                >
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {emails.map((email, index) => (
                      <span
                        key={index}
                        className="badge d-inline-flex align-items-center gap-1"
                        style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem', backgroundColor:"var(--primary-color)" }}
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveEmail(index)}
                          className="btn-close btn-close-white"
                          style={{ 
                            fontSize: '0.5rem',
                            padding: '0.25rem',
                            marginLeft: '0.25rem'
                          }}
                          aria-label="Remove"
                        />
                      </span>
                    ))}
                  </div>
                  
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={() => inputValue && handleAddEmail(inputValue)}
                    placeholder={emails.length === 0 ? 'Inserisci email e premi Invio, virgola o spazio' : ''}
                    disabled={emails.length >= 10}
                    className="form-control"
                    style={{ 
                      outline: 'none',
                      boxShadow: 'none'
                    }}
                  />
                </div>

                {error && (
                  <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
                    {error}
                  </div>
                )}

                <small className="text-white d-block mt-2">
                  {t("text.add_email_modal")}
                </small>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary text-uppercase"
                style={{ borderRadius: 25 }}
                onClick={handleClose}
              >
                {t("button.cancel")}
              </button>

              <button
                type="button"
                className="btn btn-success text-uppercase"
                style={{ borderRadius: 25 }}
                onClick={handleSubmit}
                disabled={emails.length === 0}
              >
                {t("button.submit")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailSenderModal;